import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Clock, Lock, Store } from "lucide-react";
import { getPublicRestaurants, getMaisonHome } from "../lib/api";

const GREEN_DARK = "#004A36";
const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const PIN_247 = "/brand/pin-247.png";
const HERO = "/brand/rb_hero.png";

// Repli d'IMAGE par pastille (visuels officiels) quand la sous-rubrique n'a pas encore de photo.
// La NAVIGATION reste toujours pilotée par la base (DB = source de vérité).
const BRAND_IMG = {
  burgers: "/brand/category-burgers.png",
  poulet: "/brand/category-poulet.png",
  pates: "/brand/category-pates.png",
  pizzas: "/brand/category-pizzas.png",
  boissons: "/brand/category-boissons.png",
};
const _norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const brandImgFor = (label) => {
  const n = _norm(label);
  if (n.includes("boisson")) return BRAND_IMG.boissons;
  if (n.includes("burger")) return BRAND_IMG.burgers;
  if (n.includes("poulet")) return BRAND_IMG.poulet;
  if (n.includes("pate") || n.includes("pâte")) return BRAND_IMG.pates;
  if (n.includes("pizza")) return BRAND_IMG.pizzas;
  return null;
};

export default function RestaurantBoissons() {
  const nav = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [maisonHome, setMaisonHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getMaisonHome().then(setMaisonHome).catch(() => {});
    getPublicRestaurants()
      .then(setRestaurants)
      .catch(() => setError("Impossible de charger les restaurants partenaires."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Vignettes TOUJOURS pilotées par la base (/maison/home) : rubriques resto → menu détail,
  // + une pastille Boissons → catalogue Boissons indépendant. Repli d'image par pastille seulement.
  const chips = [
    ...(maisonHome?.rubriques || []).map((r) => ({
      key: r.id, label: r.name, img: r.image_url || brandImgFor(r.name),
      to: `/restaurant-boissons/menu?category=${encodeURIComponent(r.id)}`,
    })),
    ...(maisonHome?.boissons ? [{
      key: maisonHome.boissons.id,
      label: maisonHome.boissons.name?.replace("La BASE Maison — ", "") || "Boissons",
      img: maisonHome.boissons.image_url || BRAND_IMG.boissons,
      to: `/restaurant-boissons/menu?rid=${encodeURIComponent(maisonHome.boissons.id)}`,
    }] : []),
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="rb-page">
      {/* HERO officiel (image sans texte + texte natif) */}
      <div className="relative rounded-3xl overflow-hidden mb-8" style={{ height: 220 }} data-testid="rb-hero">
        <img src={HERO} alt="Restaurant & Boissons" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.30)" }} />
        <div className="absolute left-6 bottom-6 right-6">
          <h1 className="text-3xl md:text-5xl font-extrabold text-white">Restaurant &amp; Boissons</h1>
          <p className="mt-2 text-base md:text-lg" style={{ color: "#eaf3f0" }}>Nos créations maison et nos partenaires locaux</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="flex items-center gap-2 rounded-2xl px-4 h-12 mb-10" style={{ background: "#FFFFFF", border: "1px solid #E9ECEA", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <Search size={18} color="#68736F" />
        <input data-testid="rb-search" disabled placeholder="Rechercher un plat, une boisson..." className="flex-1 bg-transparent outline-none text-sm" />
      </div>

      {/* La BASE Maison */}
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-2xl font-extrabold" style={{ color: GREEN }}>La BASE Maison</h2>
          <p className="text-sm mt-1" style={{ color: "#68736F" }}>Des recettes gourmandes, préparées chez nous</p>
        </div>
        <button onClick={() => nav("/restaurant-boissons/menu")} data-testid="rb-maison-voirtout" className="text-sm font-bold flex items-center gap-1" style={{ color: ORANGE }}>
          Voir tout <ArrowRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-5 gap-3 md:gap-4 mb-12">
        {chips.map((c) => {
          const src = c.img;
          return (
            <button key={c.key} data-testid={`rb-maison-${c.key}`} onClick={() => nav(c.to || "/restaurant-boissons/menu")}
              className="flex flex-col items-center gap-2 transition-transform hover:-translate-y-1">
              <div className="w-full rounded-xl overflow-hidden flex items-center justify-center" style={{ aspectRatio: "1 / 1", background: src ? "#E9ECEA" : GREEN_DARK }}>
                {src ? <img src={src} alt={c.label} className="w-full h-full object-cover" /> : <Store size={22} color="#fff" />}
              </div>
              <span className="text-xs md:text-sm font-semibold" style={{ color: "#101714" }}>{c.label}</span>
            </button>
          );
        })}
      </div>

      {/* Restaurants partenaires */}
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-2xl font-extrabold" style={{ color: GREEN }}>Restaurants partenaires</h2>
          <p className="text-sm mt-1" style={{ color: "#68736F" }}>Les meilleures adresses de votre région</p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="rb-loading">
          {[...Array(4)].map((_, i) => <div key={i} className="h-44 rounded-2xl animate-pulse" style={{ background: "#E9ECEA" }} />)}
        </div>
      ) : error ? (
        <div className="text-center py-10">
          <p style={{ color: "#68736F" }}>{error}</p>
          <button data-testid="rb-retry" onClick={load} className="mt-4 rounded-xl font-bold text-white px-5 py-2" style={{ background: GREEN }}>Réessayer</button>
        </div>
      ) : restaurants.length === 0 ? (
        <div className="text-center py-10" data-testid="rb-empty">
          <Store size={40} color="#cbd5e1" className="mx-auto mb-3" />
          <p style={{ color: "#68736F" }}>Aucun restaurant partenaire pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {restaurants.map((r) => (
            <button key={r.id} data-testid={`rb-partner-${r.id}`} onClick={() => nav(`/restaurant/${r.id}`)}
              className="rounded-2xl overflow-hidden text-left bg-white transition-transform hover:-translate-y-1" style={{ border: "1px solid #EFEFEC" }}>
              <div className="relative h-28 flex items-center justify-center" style={{ background: "#F2F4F3", borderBottom: "1px solid #ECEFEE" }}>
                {r.hero_image_url ? <img src={r.hero_image_url} alt={r.name} className="w-full h-full object-cover" /> : <Store size={26} color="#B8C2BE" />}
                <img src={PIN_247} alt="24/7" className="absolute top-2 left-2" style={{ height: 34 }} />
              </div>
              <div className="p-3">
                <p className="font-bold truncate" style={{ color: "#101714" }}>{r.name}</p>
                {r.cuisine && <p className="text-xs truncate" style={{ color: "#94a3b8" }}>{r.cuisine}</p>}
                <div className="flex items-center gap-2 mt-1">
                  {r.eta_label && <p className="text-xs flex items-center gap-1" style={{ color: "#68736F" }}><Clock size={12} /> {r.eta_label}</p>}
                  <Lock size={12} color={ORANGE} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
